import { useState } from 'react'

import Icon from '@components/utils/Icon'
import { IconHint } from '@components/molecules/IconHint'

import { scopedTranslation } from '@utils/I18n'

import { GhostChip } from './GhostChip'
import useRestoreRemovedAssigneeMutation from './useRestoreRemovedAssigneeMutation'

import type { AssigneeRefFields } from '../shifts/types'

const t = scopedTranslation('roster_board')

export function RemovedRow({
  shiftId,
  assignees,
  onChange,
}: {
  shiftId: string
  assignees: AssigneeRefFields[]
  onChange: () => void
}) {
  const restore = useRestoreRemovedAssigneeMutation()
  const [restoringId, setRestoringId] = useState<string | null>(null)

  if (assignees.length === 0) return null

  const handleRestore = (personId: string) => {
    setRestoringId(personId)
    restore({
      shiftId,
      personId,
      onSuccess: () => {
        setRestoringId(null)
        onChange()
      },
      onError: () => setRestoringId(null),
    })
  }

  return (
    <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-1.5">
      <span className="tw:text-xs tw:font-medium tw:text-gray-500">{t('removed_row_label')}</span>
      {assignees.map((assignee) => {
        const name = assignee.fullName ?? t('changes.unknown_volunteer')

        return (
          <GhostChip key={assignee.personId} name={name}>
            <IconHint label={t('restore_aria', { name })}>
              <button
                type="button"
                onClick={() => handleRestore(assignee.personId)}
                disabled={restoringId === assignee.personId}
                aria-label={t('restore_aria', { name })}
                className="tw:flex tw:h-6 tw:w-6 tw:shrink-0 tw:cursor-pointer tw:items-center tw:justify-center tw:rounded-full tw:text-gray-500 tw:hover:bg-success-50 tw:hover:text-success-700 tw:focus:outline-hidden tw:focus-visible:ring-2 tw:focus-visible:ring-success-400"
              >
                <Icon type="flip-backwards" className="tw:h-3.5 tw:w-3.5" aria-hidden />
              </button>
            </IconHint>
          </GhostChip>
        )
      })}
    </div>
  )
}
